import axios from 'axios';
import config from '../config';

// Configuration de base de l'API
const API_BASE_URL = config.apiUrl;

// Créer l'instance axios
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Intercepteur pour ajouter le token d'authentification
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Ajouter l'ID de session pour les utilisateurs non connectés
    const sessionId = localStorage.getItem('session_id');
    if (sessionId) {
      config.headers['X-Session-ID'] = sessionId;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les réponses
api.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Token expiré ou invalide
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Services API
export const authService = {
  // Connexion
  login: (credentials) => api.post('/login', credentials),

  // Inscription
  register: (userData) => api.post('/register', userData),

  // Déconnexion
  logout: () => api.post('/logout'),

  // Profil utilisateur
  getProfile: () => api.get('/profile'),

  // Mettre à jour le profil
  updateProfile: (profileData) => api.put('/profile', profileData),

  // Changer le mot de passe
  changePassword: (passwordData) => api.post('/change-password', passwordData),

  // Mot de passe oublié
  forgotPassword: (email) => api.post('/forgot-password', { email }),

  // Réinitialiser le mot de passe
  resetPassword: (token, passwordData) => api.post('/reset-password', { token, ...passwordData }),
};

export const productService = {
  // Obtenir tous les produits
  getProducts: (params = {}) => api.get('/products', { params }),

  // Obtenir un produit par ID
  getProduct: (id) => api.get(`/products/${id}`),

  // Produits en vedette
  getFeaturedProducts: () => api.get('/products/featured'),

  // Produits les mieux notés
  getTopRatedProducts: () => api.get('/products/top-rated'),

  // Produits en promotion
  getOnSaleProducts: () => api.get('/products/on-sale'),

  // Prix maximum
  getMaxPrice: () => api.get('/products/max-price'),

  // Produits similaires
  getSimilarProducts: (id) => api.get(`/products/${id}/similar`),

  // Recherche de produits
  searchProducts: (params) => api.get('/products/search', { params }),
};

export const categoryService = {
  // Obtenir toutes les catégories
  getCategories: () => api.get('/categories'),

  // Obtenir une catégorie par ID
  getCategory: (id) => api.get(`/categories/${id}`),

  // Catégories racines
  getRootCategories: () => api.get('/categories', { params: { top_level: true } }),
};

export const cartService = {
  // Obtenir le panier
  getCart: () => api.get('/cart'),

  // Ajouter au panier
  addToCart: (productId, quantity = 1) =>
    api.post('/cart', { product_id: productId, quantity }),

  // Mettre à jour la quantité
  updateCartItem: (itemId, quantity) =>
    api.put(`/cart/${itemId}`, { quantity }),

  // Supprimer du panier
  removeFromCart: (itemId) => api.delete(`/cart/${itemId}`),

  // Vider le panier
  clearCart: () => api.post('/cart/clear'),

  // Fusionner le panier
  mergeCart: () => api.post('/cart/merge'),

  // Résumé du panier
  getCartSummary: () => api.get('/cart/summary'),
};

export const orderService = {
  // Obtenir les commandes
  getOrders: (params = {}) => api.get('/orders', { params }),

  // Obtenir une commande
  getOrder: (id) => api.get(`/orders/${id}`),

  // Créer une commande
  createOrder: (orderData) => api.post('/orders', orderData),

  // Annuler une commande
  cancelOrder: (id, comment = '') => api.post(`/orders/${id}/cancel`, { comment }),

  // Laisser un avis
  reviewOrder: (id, reviewData) => api.post(`/orders/${id}/review`, reviewData),

  // Valider un code promo
  // shippingCost et taxRate sont ignorés côté backend - le backend utilise toujours les valeurs de la DB
  validatePromotionCode: (code, subtotal, items, shippingCost, taxRate) =>
    api.post('/orders/validate-promotion', {
      code,
      subtotal,
      items,
      // shipping_cost et tax_rate sont ignorés côté backend - toujours calculés depuis la DB
    }),
};

export const wishlistService = {
  // Obtenir tous les favoris
  getWishlist: () => api.get('/wishlist'),

  // Ajouter un produit aux favoris
  addToWishlist: (productId) => api.post('/wishlist', { product_id: productId }),

  // Retirer un produit des favoris
  removeFromWishlist: (productId) => api.delete(`/wishlist/${productId}`),

  // Vérifier si un produit est dans les favoris
  checkWishlist: (productId) => api.get(`/wishlist/check/${productId}`),
};

export const settingService = {
  // Obtenir les paramètres publics (frais de livraison, taxes, etc.)
  getPublicSettings: () => api.get('/settings/public'),
};

export const promotionService = {
  // Obtenir les promotions actives (pour Hero)
  getActivePromotions: (limit = 5) => api.get('/promotions/active', { params: { limit } }),
};

export const bannerService = {
  // Obtenir les bannières actives
  getActiveBanners: (position = 'home', limit = 5) => api.get('/banners/active', { params: { position, limit } }),
};

export const reviewService = {
  // Obtenir les avis d'un produit
  getProductReviews: (productId) => api.get(`/products/${productId}/reviews`),
  // Créer un avis
  createReview: (data) => api.post('/reviews', data),
  // Marquer un avis comme utile
  markHelpful: (reviewId) => api.post(`/reviews/${reviewId}/helpful`),
  // Soumettre un avis (alias pour createReview)
  submitReview: (data) => api.post('/reviews', data),
};

// Utilitaires
export const utils = {
  // Générer un ID de session
  generateSessionId: () => {
    return 'session_' + Math.random().toString(36).substr(2, 9) + Date.now();
  },

  // Initialiser la session
  initSession: () => {
    let sessionId = localStorage.getItem('session_id');
    if (!sessionId) {
      sessionId = utils.generateSessionId();
      localStorage.setItem('session_id', sessionId);
    }
    return sessionId;
  },

  // Sauvegarder l'authentification
  saveAuth: (token, user) => {
    localStorage.setItem('auth_token', token);
    localStorage.setItem('user', JSON.stringify(user));
  },

  // Supprimer l'authentification
  clearAuth: () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
  },

  // Obtenir l'utilisateur connecté
  getCurrentUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  // Vérifier si l'utilisateur est connecté
  isAuthenticated: () => {
    return !!localStorage.getItem('auth_token');
  },
};

// Initialiser la session au chargement
utils.initSession();

export default api;

